import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, CircularProgress, Alert, Dialog } from '@mui/material';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import { ephemerisService, archiveService } from '../../services/api';
import ShareButtons from '../../components/ShareButtons';

dayjs.locale('fr');

function DailyEphemeris() {
  const [ephemeris, setEphemeris] = useState(null);
  const [archiveImage, setArchiveImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zoomedImage, setZoomedImage] = useState(null);

  useEffect(() => {
    ephemerisService.getToday()
      .then(res => setEphemeris(res.data.data))
      .catch(() => setError("Impossible de charger l'éphéméride du jour."))
      .finally(() => setLoading(false));

    archiveService.getDaily()
      .then(res => setArchiveImage(res.data.image ? res.data : null))
      .catch(() => setArchiveImage(null));
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !ephemeris) {
    return <Alert severity="info">{error || "Aucune éphéméride disponible pour aujourd'hui."}</Alert>;
  }

  const { proverb, weekday, month } = ephemeris;

  return (
    <Paper sx={{ p: { xs: 2, sm: 4 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
            📅 Éphéméride du jour
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            {dayjs(ephemeris.date).format('dddd D MMMM YYYY')}
          </Typography>
        </Box>
        <ShareButtons
          text={proverb ? `${proverb.duala_text} — ${proverb.french_text}` : 'Éphéméride Duala du jour'}
          url="https://mes-ephemerides.com/aujourdhui"
        />
      </Box>

      {(weekday || month) && (
        <Box sx={{ mb: 3 }}>
          {weekday && (
            <Typography variant="body1">
              <strong>{weekday.duala_name}</strong> ({weekday.french_name})
            </Typography>
          )}
          {month && (
            <Typography variant="body1">
              <strong>{month.duala_name}</strong> ({month.french_name})
            </Typography>
          )}
        </Box>
      )}

      {(ephemeris.id || archiveImage) && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 3,
            mb: 3,
            alignItems: 'flex-start',
            justifyContent: 'center',
          }}
        >
          {ephemeris.id && (
            <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
              <Box
                component="img"
                src={ephemerisService.getImageUrl(ephemeris.id, 'frame')}
                alt="Éphéméride du jour"
                onClick={() => setZoomedImage(ephemerisService.getImageUrl(ephemeris.id, 'frame'))}
                sx={{ maxWidth: '100%', borderRadius: 2, boxShadow: 2, cursor: 'zoom-in' }}
              />
            </Box>
          )}
          {archiveImage && (
            <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
              <Box
                component="img"
                src={archiveService.getImageUrl(archiveImage.url)}
                alt="Image du jour"
                onClick={() => setZoomedImage(archiveService.getImageUrl(archiveImage.url))}
                sx={{ maxWidth: '100%', borderRadius: 2, boxShadow: 2, cursor: 'zoom-in' }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Image du jour
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <Dialog open={!!zoomedImage} onClose={() => setZoomedImage(null)} maxWidth={false}>
        <Box
          component="img"
          src={zoomedImage}
          alt=""
          onClick={() => setZoomedImage(null)}
          sx={{ maxWidth: '95vw', maxHeight: '90vh', display: 'block', cursor: 'zoom-out' }}
        />
      </Dialog>

      {proverb && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {proverb.duala_text}
          </Typography>
          <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'error.main', mt: 1 }}>
            {proverb.french_text}
          </Typography>
          {proverb.french_meaning && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Sens : {proverb.french_meaning}
            </Typography>
          )}
        </Box>
      )}
    </Paper>
  );
}

export default DailyEphemeris;
